import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useSuspenseQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { TriangleAlertIcon } from 'lucide-react'
import {
  AlertTitle,
  AsyncButton,
  Button,
  ColoredAlert,
  showConfirmationPrompt,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { BannerPortal, CodeSnippet, Page } from '~/components'
import { BpSpinner, EmptyState } from '~/elements'
import { useUpdateBot } from '~/hooks'
import { useWebchatEmbedScript } from '~/hooks/webchat'
import { getQueryOptions } from '~/services'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { DesktopWebchat } from '~/features/webchat/components/DesktopWebchat'
import { MobileWebchat } from '~/features/webchat/components/MobileWebchat'

const currentRoute = '/workspaces/$workspaceId/bots/$botId/webchat' as const

export const Route = createFileRoute(currentRoute)({
  component: () => <Component />,
})

function Component() {
  const { botId, workspaceId } = Route.useParams()
  const { mutate: updateBot, isPending: isPendingUpdateBot } = useUpdateBot()

  const [
    {
      data: { integration: latestWebchatIntegration },
    },
    { data: bot },
  ] = useSuspenseQueries({
    queries: [
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_/integrations/$integrationName/$version', {
        workspaceId,
        botId,
        integrationName: 'webchat',
        version: 'latest',
      }),
      getQueryOptions('workspaces_/$workspaceId_/bots_/$botId_', { workspaceId, botId }),
    ],
  })

  const webchatIntegration = Object.values(bot.integrations).find((integration) => integration.name === 'webchat')
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])
  const _hasHydrated = useConfigStore((state) => state._hasHydrated)

  const { embedScript } = useWebchatEmbedScript({ botId, workspaceId })

  const isMobile = useIsMobile()

  if (!webchatIntegration) {
    return (
      <Page title={'Webchat'}>
        <div className="p-8">
          <EmptyState
            title="Webchat not installed"
            description="You must install the Webchat integration to interact with it."
            primaryAction={
              <Button
                onClick={() =>
                  updateBot({
                    id: bot.id,
                    workspaceId,
                    integrations: { [latestWebchatIntegration.id]: { enabled: true, configuration: {} } },
                  })
                }
              >
                Install Webchat
              </Button>
            }
          />
        </div>
      </Page>
    )
  }

  const handleUpgrade = async () => {
    const result = await showConfirmationPrompt(
      <div className="flex flex-col gap-2">
        <span className="text-sm text-muted-foreground">
          Please copy and paste the following code on your webpage before upgrading:
        </span>
        <CodeSnippet code={embedScript ?? ''} />
      </div>,
      { title: 'Upgrade Webchat', showCancel: true, confirmLabel: 'Upgrade', size: 'max-w-4xl' }
    )

    if (result) {
      updateBot({
        id: bot.id,
        workspaceId,
        integrations: { [latestWebchatIntegration.id]: { enabled: true } },
      })
    }
  }

  return (
    <>
      <div className="flex items-center">
        {latestWebchatIntegration.version !== webchatIntegration?.version && (
          <>
            <BannerPortal>
              <ColoredAlert color="amber" className="my-4 xl:w-[47.5%]">
                <TriangleAlertIcon className="size-5" />
                <AlertTitle>
                  You&apos;re using an older version of Webchat that will no longer function as of{' '}
                  <strong>Dec 31, 2025</strong>. Click{' '}
                  <strong
                    className="cursor-pointer hover:underline"
                    onClick={() => {
                      if (isPendingUpdateBot) return
                      handleUpgrade()
                    }}
                  >
                    Upgrade{' '}
                  </strong>
                  to generate a new embed snippet and replace your existing Webchat code. After upgrading, your Webchat
                  will be unavailable until you replace the deprecated embed snippet.
                </AlertTitle>
              </ColoredAlert>
            </BannerPortal>
            <div className="ml-5 flex gap-2">
              <h1 className="text-2xl font-semibold">Webchat</h1>
              <AsyncButton size="sm" loading={isPendingUpdateBot} onClick={handleUpgrade}>
                Upgrade
              </AsyncButton>
            </div>
          </>
        )}
      </div>
      {_hasHydrated ? (
        <div className="flex h-full flex-1 flex-col bg-background md:py-6">
          {isMobile ? (
            <MobileWebchat botId={botId} workspaceId={workspaceId}>
              <Outlet />
            </MobileWebchat>
          ) : (
            <DesktopWebchat botId={botId} workspaceId={workspaceId}>
              <Outlet />
            </DesktopWebchat>
          )}
        </div>
      ) : (
        <div className="mx-auto size-20 h-96">
          <BpSpinner />
        </div>
      )}
    </>
  )
}
